import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { requireAccess } from '../lib/guards';
import { useSession } from '../lib/session';
import {
  formatMinuten,
  zeiterfassungApi,
  type Antrag,
  type AntragStatus,
  type EintragTyp,
  type StempelStatus,
  type TagesSummary,
} from '../lib/zeiterfassung-api';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/zeiterfassung/')({
  beforeLoad: () => requireAccess('/api/v1/zeiterfassung/status'),
  component: ZeiterfassungPage,
});

const TYP_LABEL: Record<EintragTyp, string> = {
  kommen: 'Kommen',
  gehen: 'Gehen',
  pause_start: 'Pause',
  pause_ende: 'Pause beenden',
};

const ZUSTAND_FARBE: Record<StempelStatus['zustand'], string> = {
  abwesend: 'bg-slate-100 text-slate-700 border-slate-200',
  arbeitend: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  pause: 'bg-amber-100 text-amber-800 border-amber-200',
};

const ZUSTAND_LABEL: Record<StempelStatus['zustand'], string> = {
  abwesend: 'Abwesend',
  arbeitend: 'Im Dienst',
  pause: 'In Pause',
};

function ZeiterfassungPage(): JSX.Element {
  const qc = useQueryClient();
  const heute = new Date().toISOString().slice(0, 10);

  const statusQ = useQuery({
    queryKey: ['zeiterfassung', 'status'],
    queryFn: () => zeiterfassungApi.status(),
    refetchInterval: 30_000, // self-refresh
  });
  const tagQ = useQuery({
    queryKey: ['zeiterfassung', 'tag', heute],
    queryFn: () => zeiterfassungApi.getTag(heute),
    refetchInterval: 30_000,
  });
  const antraegeQ = useQuery({
    queryKey: ['zeiterfassung', 'antraege', 'eigene'],
    queryFn: () => zeiterfassungApi.antraegeListe(),
  });

  const stempel = useMutation({
    mutationFn: (typ: EintragTyp) => zeiterfassungApi.stempel({ typ }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zeiterfassung'] });
    },
  });

  const status = statusQ.data?.item;
  const tag = tagQ.data?.item;
  const antraege = antraegeQ.data?.items ?? [];

  const session = useSession();
  const istBuero = session.data?.role === 'admin' || session.data?.role === 'buero';

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="Zeiterfassung"
        description="Stempeln, eigener Tag, Anträge."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/zeiterfassung/monat"
              className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Monatsübersicht
            </Link>
            {istBuero ? (
              <Link
                to="/zeiterfassung/admin"
                className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Anträge prüfen
              </Link>
            ) : null}
            <Link
              to="/zeiterfassung/antrag"
              className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              + Antrag stellen
            </Link>
          </div>
        }
      />

      {statusQ.isError ? <ErrorBanner error={statusQ.error} /> : null}
      {stempel.isError ? <ErrorBanner error={stempel.error} /> : null}

      {status ? (
        <StatusKarte status={status} />
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-slate-500">
          lädt …
        </div>
      )}

      {status ? (
        <ButtonReihe
          status={status}
          onStempel={(typ) => stempel.mutate(typ)}
          pending={stempel.isPending}
        />
      ) : null}

      {tag ? <TageshistorieKarte tag={tag} /> : null}

      {antraege.length > 0 ? <EigeneAntraegeKarte antraege={antraege.slice(0, 5)} /> : null}
    </div>
  );
}

function StatusKarte({ status }: { status: StempelStatus }): JSX.Element {
  const [, forceTick] = useState(0);
  // Live-Timer: Re-Render alle 30s, damit "Seit-Dauer" aktuell bleibt.
  useEffect(() => {
    if (status.zustand === 'abwesend') return;
    const id = setInterval(() => forceTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [status.zustand]);

  const seitText = status.seit ? formatSeit(status.seit) : null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <span
            className={`inline-flex items-center rounded-md border px-3 py-1 text-sm font-medium ${ZUSTAND_FARBE[status.zustand]}`}
          >
            {ZUSTAND_LABEL[status.zustand]}
          </span>
          {seitText ? <div className="mt-2 text-sm text-slate-600">seit {seitText}</div> : null}
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-slate-500">Heute Netto</div>
          <div className="text-2xl font-semibold text-slate-900">
            {formatMinuten(status.heuteNettoMinuten)} h
          </div>
        </div>
      </div>
    </div>
  );
}

function ButtonReihe({
  status,
  onStempel,
  pending,
}: {
  status: StempelStatus;
  onStempel: (typ: EintragTyp) => void;
  pending: boolean;
}): JSX.Element {
  // Primaere Aktion = naechsteAktion. Wenn arbeitend, sekundaer = pause_start.
  const primary = status.naechsteAktion;
  const secondary: EintragTyp | null = status.zustand === 'arbeitend' ? 'pause_start' : null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <button
        type="button"
        disabled={pending}
        onClick={() => onStempel(primary)}
        className={`flex h-32 items-center justify-center rounded-xl text-2xl font-semibold text-white shadow-md transition disabled:opacity-50 ${primaryColorClass(primary)}`}
      >
        {TYP_LABEL[primary]}
      </button>
      {secondary ? (
        <button
          type="button"
          disabled={pending}
          onClick={() => onStempel(secondary)}
          className="flex h-32 items-center justify-center rounded-xl bg-amber-500 text-2xl font-semibold text-white shadow-md transition hover:bg-amber-600 disabled:opacity-50"
        >
          {TYP_LABEL[secondary]}
        </button>
      ) : (
        <div className="hidden sm:block" /> // Gridplatzhalter
      )}
    </div>
  );
}

function primaryColorClass(typ: EintragTyp): string {
  switch (typ) {
    case 'kommen':
      return 'bg-emerald-600 hover:bg-emerald-700';
    case 'gehen':
      return 'bg-slate-700 hover:bg-slate-800';
    case 'pause_ende':
      return 'bg-amber-600 hover:bg-amber-700';
    case 'pause_start':
      return 'bg-amber-500 hover:bg-amber-600';
  }
}

function TageshistorieKarte({ tag }: { tag: TagesSummary }): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <h2 className="text-sm font-semibold text-slate-700">Heute</h2>
        <div className="text-xs text-slate-500">
          Brutto {formatMinuten(tag.bruttoMinuten)} · Pause{' '}
          {formatMinuten(tag.effektivePauseMinuten)} · Netto{' '}
          <span className="font-semibold text-slate-700">{formatMinuten(tag.nettoMinuten)}</span>{' '}
          (Soll {formatMinuten(tag.sollMinuten)},{' '}
          <span className={tag.diffMinuten >= 0 ? 'text-emerald-700' : 'text-red-700'}>
            {tag.diffMinuten >= 0 ? '+' : ''}
            {formatMinuten(tag.diffMinuten)}
          </span>
          )
        </div>
      </div>
      <ul className="divide-y divide-slate-100 text-sm">
        {tag.eintraege.length === 0 ? (
          <li className="px-5 py-4 text-slate-500">Heute noch keine Stempel.</li>
        ) : (
          tag.eintraege.map((e) => (
            <li key={e.id} className="flex items-center justify-between px-5 py-2">
              <span className="font-medium text-slate-700">{TYP_LABEL[e.typ]}</span>
              <span className="font-mono text-xs text-slate-600">
                {new Date(e.zeitpunkt).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

const ANTRAG_TYP_LABEL: Record<Antrag['typ'], string> = {
  urlaub: 'Urlaub',
  abwesenheit: 'Abwesenheit',
  korrektur: 'Korrektur',
  manueller_eintrag: 'Manueller Eintrag',
};

const ANTRAG_STATUS_FARBE: Record<AntragStatus, string> = {
  ausstehend: 'bg-amber-100 text-amber-800 border-amber-200',
  genehmigt: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  abgelehnt: 'bg-red-100 text-red-800 border-red-200',
};

function EigeneAntraegeKarte({ antraege }: { antraege: Antrag[] }): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-3">
        <h2 className="text-sm font-semibold text-slate-700">Meine Anträge</h2>
      </div>
      <ul className="divide-y divide-slate-100 text-sm">
        {antraege.map((a) => (
          <li key={a.id} className="flex items-center justify-between px-5 py-2">
            <div className="flex flex-col">
              <span className="font-medium text-slate-700">{ANTRAG_TYP_LABEL[a.typ]}</span>
              <span className="text-xs text-slate-500">
                {formatGermanDateFromTimestamp(a.createdAt)}
              </span>
            </div>
            <span
              className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${ANTRAG_STATUS_FARBE[a.status]}`}
            >
              {a.status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatSeit(iso: string): string {
  const dauerMs = Date.now() - new Date(iso).getTime();
  if (dauerMs < 0)
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const min = Math.floor(dauerMs / 60_000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h} h ${m} min`;
}
